import PropTypes from "prop-types";
import { useEffect } from "react";

// form
import { useFormContext, Controller } from "react-hook-form";
// @mui
import { TextField, ThemeProvider, createTheme } from "@mui/material";
import React from "react";
import { NumericFormat } from "react-number-format";
import { IMaskInput } from "react-imask";
import { DatePicker } from "@mui/x-date-pickers";
import { PRIMARY } from "../../theme/palette";
import dayjs from "dayjs";

// ----------------------------------------------------------------------
const TextMaskCustom = React.forwardRef(function TextMaskCustom(props, ref) {
  const { onChange, ...other } = props;
  return (
    <IMaskInput
      {...other}
      mask="(#00) 000-0000"
      definitions={{
        "#": /[1-9]/,
      }}
      inputRef={ref}
      onAccept={(value) => onChange({ target: { name: props.name, value } })}
      overwrite
    />
  );
});

TextMaskCustom.propTypes = {
  name: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
};

const SSNMaskCustom = React.forwardRef(function SSNMaskCustom(props, ref) {
  const { onChange, ...other } = props;
  return (
    <IMaskInput
      {...other}
      mask="xxx-xx-xx00"
      definitions={{
        x: {
          mask: "0",
          displayChar: "x",
        },
      }}
      // mask="000-00-0000"
      inputRef={ref}
      onAccept={(value) => onChange({ target: { name: props.name, value } })}
      overwrite
    />
  );
});

SSNMaskCustom.propTypes = {
  name: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
};

const PasswordFormatCustom = React.forwardRef(function PasswordFormatCustom(
  props,
  ref
) {
  const { onChange, ...other } = props;
  return (
    <IMaskInput
      {...other}
      mask="****************************************"
      definitions={{
        "*": {
          mask: /[a-zA-Z0-9]/,
          displayChar: "*",
        },
      }}
      inputRef={ref}
      onAccept={(value) => onChange({ target: { name: props.name, value } })}
      overwrite
    />
  );
});

PasswordFormatCustom.propTypes = {
  name: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
};

RHFTextField.propTypes = {
  name: PropTypes.string,
  helperText: PropTypes.node,
};

const NumericFormatCustom = React.forwardRef(function NumericFormatCustom(
  props,
  ref
) {
  const { onChange, ...other } = props;
  return (
    <NumericFormat
      {...other}
      getInputRef={ref}
      onValueChange={(values) => {
        onChange({
          target: {
            name: props.name,
            value: +values.floatValue,
          },
        });
      }}
      thousandSeparator
      valueIsNumericString
      prefix="$"
    />
  );
});

NumericFormatCustom.propTypes = {
  name: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
};

const percentageFormatCustom = React.forwardRef(function percentageFormatCustom(
  props,
  ref
) {
  const { onChange, ...other } = props;
  return (
    <NumericFormat
      {...other}
      getInputRef={ref}
      decimalScale={2}
      isAllowed={(values) => {
        const { formattedValue, floatValue } = values;
        return formattedValue === "" || floatValue <= 100;
      }}
      onValueChange={(values) => {
        onChange({
          target: {
            name: props.name,
            value: +values.floatValue,
          },
        });
      }}
      suffix="%"
    />
  );
});

percentageFormatCustom.propTypes = {
  name: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
};

const percentageFormatCustomNoDecimal = React.forwardRef(
  function percentageFormatCustomNoDecimal(props, ref) {
    const { onChange, ...other } = props;
    return (
      <NumericFormat
        {...other}
        getInputRef={ref}
        decimalScale={0}
        isAllowed={(values) => {
          const { formattedValue, floatValue } = values;
          return formattedValue === "" || floatValue <= 100;
        }}
        onValueChange={(values) => {
          onChange({
            target: {
              name: props.name,
              value: +values.floatValue,
            },
          });
        }}
        suffix="%"
      />
    );
  }
);

percentageFormatCustomNoDecimal.propTypes = {
  name: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
};

const IntegerFormatCustom = React.forwardRef(function IntegerFormatCustom(
  props,
  ref
) {
  const { onChange, ...other } = props;
  return (
    <NumericFormat
      {...other}
      getInputRef={ref}
      decimalScale={0}
      allowNegative={false}
      onValueChange={(values) => {
        onChange({
          target: {
            name: props.name,
            value: +values.floatValue,
          },
        });
      }}
    />
  );
});

IntegerFormatCustom.propTypes = {
  name: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
};

const ZipCodeFormatCustom = React.forwardRef(function ZipCodeFormatCustom(
  props,
  ref
) {
  const { onChange, ...other } = props;
  return (
    <NumericFormat
      {...other}
      getInputRef={ref}
      decimalScale={0}
      allowNegative={false}
      onValueChange={(values) => {
        onChange({
          target: {
            name: props.name,
            value: Math.floor(+values.floatValue),
          },
        });
      }}
      format={(value) => value.toString().padStart(5, "0")} // Format as 5-digit number with leading zeros
    />
  );
});

ZipCodeFormatCustom.propTypes = {
  name: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
};

const percentageFormat3Custom3 = React.forwardRef(
  function percentageFormatCustom(props, ref) {
    const { onChange, ...other } = props;
    return (
      <NumericFormat
        {...other}
        getInputRef={ref}
        decimalScale={3}
        isAllowed={(values) => {
          const { formattedValue, floatValue } = values;
          return formattedValue === "" || floatValue <= 100;
        }}
        onValueChange={(values) => {
          onChange({
            target: {
              name: props.name,
              value: +values.floatValue,
            },
          });
        }}
        suffix="%"
      />
    );
  }
);

percentageFormat3Custom3.propTypes = {
  name: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
};

const percentageFormat2Custom2 = React.forwardRef(
  function percentageFormatCustom(props, ref) {
    const { onChange, ...other } = props;
    return (
      <NumericFormat
        {...other}
        getInputRef={ref}
        decimalScale={2}
        isAllowed={(values) => {
          const { formattedValue, floatValue } = values;
          return formattedValue === "" || floatValue <= 100;
        }}
        onValueChange={(values) => {
          onChange({
            target: {
              name: props.name,
              value: +values.floatValue,
            },
          });
        }}
        suffix="%"
      />
    );
  }
);

percentageFormat2Custom2.propTypes = {
  name: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
};

const formatCustom1DecimalFor1 = React.forwardRef(
  function percentageFormatCustom1Decimal(props, ref) {
    const { onChange, ...other } = props;
    return (
      <NumericFormat
        {...other}
        getInputRef={ref}
        decimalScale={1}
        allowNegative={false}
        onValueChange={(values) => {
          onChange({
            target: {
              name: props.name,
              value: +values.floatValue,
            },
          });
        }}
        suffix="%"
      />
    );
  }
);

formatCustom1DecimalFor1.propTypes = {
  name: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
};

const CreditScoreInput = React.forwardRef(function CreditScoreInput(
  { onChange, value, ...other },
  ref
) {
  const maskOptions = {
    mask: "000",
    min: 300,
    max: 850,
  };

  return (
    <>
      <IMaskInput
        {...other}
        {...maskOptions}
        value={value ? value.toString() : null}
        unmask={false}
        onAccept={(maskedValue) => {
          const numericValue = maskedValue.replace(/\D/g, "");
          onChange({
            target: {
              name: other.name,
              value: numericValue,
            },
          });
        }}
        inputRef={ref}
      />
    </>
  );
});

CreditScoreInput.propTypes = {
  onChange: PropTypes.func.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

export default function RHFTextField({
  currency,
  phone,
  name,
  percentage,
  helperText,
  decimal3,
  decimal2,
  type,
  integer,
  percentageNoDecimal,
  disabled,
  defaultValue,
  ssnInput,
  zipcode,
  decimal1,
  creditScore,
  params,
  advPassword,
  ...other
}) {
  const { control, setValue } = useFormContext();

  const theme = createTheme({
    overrides: {
      borderRadius: "8px",
      MuiPickersArrowSwitcher: {
        iconButton: {
          color: "blue",
        },
      },
    },
  });

  useEffect(() => {
    if (defaultValue !== undefined) {
      setValue(name, defaultValue);
    }
  }, [defaultValue, name, setValue]);

  return (
    <>
      {type !== "date" ? (
        <Controller
          name={name}
          control={control}
          {...other}
          render={({
            field: { onBlur, value, ...rest },
            fieldState: { error },
          }) => (
            <TextField
              {...rest}
              onBlur={onBlur}
              fullWidth
              disabled={disabled}
              type={type}
              {...other}
              InputProps={{
                inputComponent: currency
                  ? NumericFormatCustom
                  : phone
                  ? TextMaskCustom
                  : ssnInput
                  ? SSNMaskCustom
                  : decimal1
                  ? formatCustom1DecimalFor1
                  : percentageNoDecimal
                  ? percentageFormatCustomNoDecimal
                  : decimal3
                  ? percentageFormat3Custom3
                  : percentage
                  ? percentageFormatCustom
                  : integer
                  ? IntegerFormatCustom
                  : advPassword
                  ? PasswordFormatCustom
                  : decimal2
                  ? percentageFormat2Custom2
                  : creditScore
                  ? CreditScoreInput
                  : "",
              }}
              value={value ?? ""}
              error={!!error}
              helperText={error ? error?.message : helperText}
              {...other}
            />
          )}
        />
      ) : (
        <Controller
          name={name}
          control={control}
          {...other}
          render={({
            field: { onBlur, onChange, value, ...rest },
            fieldState: { error },
          }) => (
            <ThemeProvider theme={theme}>
              <DatePicker
                sx={{
                  svg: !disabled && { color: PRIMARY.blue },
                }}
                onChange={(event) => {
                  let date = dayjs(event?.["$d"]).format("MM/DD/YYYY");
                  onChange(date);
                }}
                {...other}
                value={value ? value : null}
                disabled={disabled}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    sx={{
                      "& .MuiInputBase-root": {
                        borderRadius: "8px !important",
                        color: PRIMARY.blue,
                      },
                      "& .MuiOutlinedInput-root": {
                        "&:hover fieldset": !disabled && {
                          borderColor: PRIMARY.blue,
                        },
                        "& fieldset": {
                          borderColor: PRIMARY.lightBlue,
                        },
                      },
                      svg: !disabled && {
                        fill: PRIMARY.blue,
                        color: PRIMARY.blue,
                      },
                    }}
                    {...rest}
                    onBlur={onBlur}
                    fullWidth
                    disabled={disabled}
                    error={!!error}
                    helperText={error ? error?.message : helperText}
                    {...other}
                  />
                )}
              />
            </ThemeProvider>
          )}
        />
      )}
    </>
  );
}
